import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getMesProjets, proposerJalon } from "../services/api";
import IconeV2 from "../components/IconeV2";
import {
  LIBELLES_STATUT, LIBELLES_ROLE_PROJET, LIBELLES_STATUT_JALON, classeStatutProjet, classeStatutJalon,
  formatDate, largeurJauge,
} from "../services/projet";

// ===================================================================
// The Crowd v2 — Mon activite › Mes projets
//
// Refonte du module Projets : les projets dont je suis acteur principal
// (mes objectifs) ou secondaire (en appui). Avancement, etapes, jalons
// et rappels de retard ; l'acteur principal propose un jalon atteint
// directement depuis la carte. Un clic ouvre la fiche projet.
// Bascule Cartes / Liste comme Taches, choix memorise.
// ===================================================================

const VUE_STORAGE_KEY = "thecrowd-v2-vue-mes-projets";

function vueInitiale() {
  try {
    const v = window.localStorage.getItem(VUE_STORAGE_KEY);
    if (v === "cartes" || v === "liste") return v;
  } catch {
    // stockage indisponible : vue par defaut
  }
  return "cartes";
}

function Jauge({ valeur }) {
  return (
    <div className="v2-projet-jauge">
      <span className="v2-barres-piste">
        <span className="v2-barres-valeur v2-ton-fort" style={{ width: largeurJauge(valeur) }} />
      </span>
      <span className="v2-projet-jauge-valeur">{valeur || 0} %</span>
    </div>
  );
}

function Badges({ p }) {
  return (
    <>
      <span className={classeStatutProjet(p.statut)}>{LIBELLES_STATUT[p.statut] || p.statut}</span>
      {p.en_retard && <span className="v2-statut v2-statut-danger">En retard</span>}
      {p.mon_role === "secondaire" && <span className="v2-statut">{LIBELLES_ROLE_PROJET.secondaire}</span>}
    </>
  );
}

function ResumeEtapes({ p }) {
  if (!p.etapes_total) return <span className="v2-cellule-discrete">Aucune étape</span>;
  return (
    <span className="v2-cellule-discrete">
      {p.etapes_terminees} / {p.etapes_total} étapes terminées
      {p.etapes_en_retard > 0 && <span className="v2-texte-danger"> · {p.etapes_en_retard} en retard</span>}
    </span>
  );
}

// --- Vue cartes ---

function CarteProjet({ p, onOuvrir, onProposer, enCours }) {
  const principal = p.mon_role === "principal";
  const aProposer = principal ? p.jalons.filter((j) => j.statut === "en_cours") : [];
  const autres = p.jalons.filter((j) => j.statut !== "en_cours" || !principal);

  return (
    <article className="v2-tache v2-mes-projet">
      <div className="v2-tache-corps">
        <div className="v2-tache-entete">
          <h3 className="v2-tache-nom">
            <button type="button" className="v2-lien-ligne" onClick={onOuvrir}>{p.nom}</button>
          </h3>
        </div>
        <div className="v2-tache-meta"><Badges p={p} /></div>
        <div className="v2-tache-suivi">
          <Jauge valeur={p.avancement} />
          <ResumeEtapes p={p} />
          <span className="v2-cellule-discrete">
            Chef : {p.chef_projet_nom || "—"} · Fin prévue : {formatDate(p.date_fin_prevue)}
          </span>
        </div>

        {p.jalons.length > 0 && (
          <div className="v2-mes-jalons">
            <span className="v2-label">Jalons · {p.jalons_atteints} / {p.jalons.length} atteints</span>
            <ul className="v2-mes-jalons-liste">
              {autres.map((j) => (
                <li key={j.id}>
                  <span className={classeStatutJalon(j.statut)}>
                    {j.statut === "atteint" ? `Atteint le ${formatDate(j.atteint_le)}` : LIBELLES_STATUT_JALON[j.statut]}
                  </span>
                  <span className="v2-mes-jalon-intitule">{j.intitule}</span>
                </li>
              ))}
              {aProposer.map((j) => (
                <li key={j.id}>
                  <button type="button" className="v2-btn v2-btn-secondaire v2-btn-compact" disabled={enCours === j.id}
                    onClick={() => onProposer(j)}>
                    {enCours === j.id ? "…" : "Proposer atteint"}
                  </button>
                  <span className="v2-mes-jalon-intitule">{j.intitule}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div className="v2-collab-actions">
        <button type="button" className="v2-btn v2-btn-secondaire" onClick={onOuvrir}>
          Ouvrir la fiche <IconeV2 nom="chevronDroite" />
        </button>
      </div>
    </article>
  );
}

// --- Vue liste ---

function VueListe({ projets, onOuvrir }) {
  return (
    <div className="v2-panneau v2-tableau-conteneur">
      <table className="v2-tableau v2-tableau-cliquable">
        <thead>
          <tr>
            <th scope="col">Projet</th>
            <th scope="col">Statut</th>
            <th scope="col" className="v2-projet-col-avancement">Avancement</th>
            <th scope="col">Étapes</th>
            <th scope="col" className="v2-num">Jalons atteints</th>
            <th scope="col">Chef</th>
            <th scope="col">Fin prévue</th>
          </tr>
        </thead>
        <tbody>
          {projets.map((p) => (
            <tr key={p.id} onClick={() => onOuvrir(p)}>
              <td>
                <button type="button" className="v2-lien-ligne" onClick={(e) => { e.stopPropagation(); onOuvrir(p); }}>{p.nom}</button>
              </td>
              <td><span className="v2-mes-badges"><Badges p={p} /></span></td>
              <td><Jauge valeur={p.avancement} /></td>
              <td><ResumeEtapes p={p} /></td>
              <td className="v2-num">{p.jalons.length ? `${p.jalons_atteints} / ${p.jalons.length}` : "—"}</td>
              <td className="v2-cellule-discrete">{p.chef_projet_nom || "—"}</td>
              <td className="v2-cellule-discrete">{formatDate(p.date_fin_prevue)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --- Page ---

function MesProjetsV2({ collaborateur }) {
  const navigate = useNavigate();
  const [projets, setProjets] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [message, setMessage] = useState(null);
  const [inclureTermines, setInclureTermines] = useState(false);
  const [vue, setVue] = useState(vueInitiale);
  const [proposition, setProposition] = useState(null); // id du jalon en cours de proposition

  useEffect(() => {
    try {
      window.localStorage.setItem(VUE_STORAGE_KEY, vue);
    } catch {
      // cf. vueInitiale
    }
  }, [vue]);

  const charger = useCallback(() => {
    if (!collaborateur) return Promise.resolve();
    setErreur(null);
    return getMesProjets(collaborateur.id, inclureTermines)
      .then(setProjets)
      .catch((err) => setErreur(`Projets non chargés : ${err.message}`));
  }, [collaborateur, inclureTermines]);

  useEffect(() => {
    charger();
  }, [charger]);

  if (!collaborateur) {
    return (
      <div className="v2-page v2-page-module">
        <div className="v2-msg v2-msg-erreur">Compte non configuré. Contactez votre manager.</div>
      </div>
    );
  }

  const ouvrir = (p) => navigate(`/v2/projets/${p.id}`);

  const proposer = async (jalon) => {
    setProposition(jalon.id);
    setMessage(null);
    try {
      await proposerJalon(jalon.id, collaborateur.id);
      setMessage({ type: "succes", texte: `« ${jalon.intitule} » est proposé : le chef de projet va le confirmer.` });
      await charger();
    } catch (err) {
      setMessage({ type: "erreur", texte: `Proposition impossible : ${err.message}` });
    } finally {
      setProposition(null);
    }
  };

  const liste = projets || [];
  const principaux = liste.filter((p) => p.mon_role === "principal");
  const secondaires = liste.filter((p) => p.mon_role === "secondaire");
  const projetsEnRetard = liste.filter((p) => p.en_retard);
  const etapesEnRetard = liste.reduce((s, p) => s + (p.etapes_en_retard || 0), 0);

  const renderGrille = (groupe) => (
    <div className="v2-tache-grille">
      {groupe.map((p) => (
        <CarteProjet key={p.id} p={p} onOuvrir={() => ouvrir(p)} onProposer={proposer} enCours={proposition} />
      ))}
    </div>
  );

  return (
    <div className="v2-page v2-page-module">
      <header className="v2-module-entete">
        <h1 className="v2-module-titre">Mes projets</h1>
        <p className="v2-sous-titre">
          Mes objectifs, fixés et suivis par mon manager, et les projets auxquels je contribue en appui.
        </p>
      </header>

      <div className="v2-barre-outils">
        <label className="v2-case-simple">
          <input type="checkbox" checked={inclureTermines} onChange={(e) => setInclureTermines(e.target.checked)} />
          <span>Afficher aussi les projets terminés et abandonnés</span>
        </label>
        <div className="v2-barre-outils-droite">
          <span className="v2-total-semaine">{liste.length} projet{liste.length > 1 ? "s" : ""}</span>
          <div className="v2-bascule" role="group" aria-label="Affichage">
            <button type="button" className={`v2-bascule-choix${vue === "cartes" ? " actif" : ""}`} aria-pressed={vue === "cartes"} onClick={() => setVue("cartes")}>
              <IconeV2 nom="tableau" taille={13} /> Cartes
            </button>
            <button type="button" className={`v2-bascule-choix${vue === "liste" ? " actif" : ""}`} aria-pressed={vue === "liste"} onClick={() => setVue("liste")}>
              <IconeV2 nom="liste" taille={13} /> Liste
            </button>
          </div>
        </div>
      </div>

      {(projetsEnRetard.length > 0 || etapesEnRetard > 0) && (
        <div className="v2-dash-alertes">
          <h2 className="v2-bloc-titre">Rappels</h2>
          <ul className="v2-dash-alertes-liste">
            {projetsEnRetard.map((p) => (
              <li key={p.id}>
                <button type="button" className="v2-lien-ligne" onClick={() => ouvrir(p)}>{p.nom}</button> : date de fin prévue dépassée ({formatDate(p.date_fin_prevue)}).
              </li>
            ))}
            {etapesEnRetard > 0 && (
              <li>
                {etapesEnRetard} étape{etapesEnRetard > 1 ? "s" : ""} en retard sur mes projets :{" "}
                <button type="button" className="v2-lien-ligne" onClick={() => navigate("/v2/activite/etapes")}>voir Mes étapes</button>.
              </li>
            )}
          </ul>
        </div>
      )}

      {erreur && <div className="v2-msg v2-msg-erreur v2-marge-bas">{erreur}</div>}
      {message && (
        <div className={`v2-msg ${message.type === "erreur" ? "v2-msg-erreur" : "v2-msg-succes"} v2-marge-bas`} role="status">
          {message.texte}
        </div>
      )}

      {!projets && !erreur && <div className="v2-chargement">Chargement…</div>}

      {projets && liste.length === 0 && (
        <div className="v2-panneau"><p className="v2-vide">Aucun projet pour l'instant.</p></div>
      )}

      {projets && liste.length > 0 && vue === "liste" && <VueListe projets={liste} onOuvrir={ouvrir} />}

      {projets && liste.length > 0 && vue === "cartes" && (
        <>
          {principaux.length > 0 && (
            <section className="v2-tache-section" aria-labelledby="v2-mes-principal">
              <div className="v2-section-titre">
                <h2 id="v2-mes-principal">Mes objectifs</h2>
                <span className="v2-section-filet" />
                <span className="v2-cellule-discrete">{principaux.length}</span>
              </div>
              {renderGrille(principaux)}
            </section>
          )}
          {secondaires.length > 0 && (
            <section className="v2-tache-section" aria-labelledby="v2-mes-secondaire">
              <div className="v2-section-titre">
                <h2 id="v2-mes-secondaire">En appui</h2>
                <span className="v2-section-filet" />
                <span className="v2-cellule-discrete">{secondaires.length}</span>
              </div>
              {renderGrille(secondaires)}
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default MesProjetsV2;
